import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { getEnabledModules, requireTenantConfig } from '../tenancy/resolver.js';
import { resolveTenantIdFromHostname } from '../registry/tenants.js';
import { fetchPlatformBootstrap } from '../api/platformClient.js';

const TenantContext = createContext(null);

function localFallback(tenantId) {
  const config = requireTenantConfig(tenantId);
  return {
    tenant: {
      id: config.id,
      name: config.name,
      shortName: config.shortName,
      tagline: config.tagline,
      vertical: config.vertical,
      location: config.location,
      phone: config.phone,
      emergency: config.emergency,
      hours: config.hours,
      domains: config.domains || [],
      channels: config.channels || {},
      modules: getEnabledModules(config.id),
      product: {
        platform: 'QorIAX Universe',
        tenantProduct: config.shortName || config.name
      }
    },
    modules: getEnabledModules(config.id)
  };
}

function resolveInitialTenantId(initialTenantId) {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const hostTenantId = resolveTenantIdFromHostname(hostname);
  if (hostTenantId) return hostTenantId;

  if (initialTenantId) {
    try {
      return requireTenantConfig(initialTenantId).id;
    } catch {
      // Invalid explicit input must not become an implicit tenant.
    }
  }

  if (typeof window !== 'undefined') {
    const storedTenantId = window.localStorage.getItem('tenant_id');
    if (storedTenantId) {
      try {
        return requireTenantConfig(storedTenantId).id;
      } catch {
        window.localStorage.removeItem('tenant_id');
      }
    }
  }

  return null;
}

export function TenantProvider({ children, initialTenantId = null }) {
  const [tenantId, setTenantIdState] = useState(() => resolveInitialTenantId(initialTenantId));
  const [tenant, setTenant] = useState(() => tenantId ? localFallback(tenantId).tenant : null);
  const [modules, setModules] = useState(() => tenantId ? localFallback(tenantId).modules : []);
  const [loading, setLoading] = useState(Boolean(tenantId));
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function loadTenant() {
      if (!tenantId) {
        setTenant(null);
        setModules([]);
        setLoading(false);
        setError(null);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const bootstrap = await fetchPlatformBootstrap(tenantId);
        if (cancelled) return;
        setTenant(bootstrap.tenant);
        setModules(bootstrap.modules);
      } catch (err) {
        if (cancelled) return;
        const fallback = localFallback(tenantId);
        setTenant(fallback.tenant);
        setModules(fallback.modules);
        setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadTenant();
    return () => { cancelled = true; };
  }, [tenantId]);

  const value = useMemo(() => ({
    tenantId,
    tenant,
    modules,
    loading,
    error,
    setTenantId: (nextTenantId) => {
      if (!nextTenantId) {
        if (typeof window !== 'undefined') window.localStorage.removeItem('tenant_id');
        setTenantIdState(null);
        return;
      }
      const config = requireTenantConfig(nextTenantId);
      if (typeof window !== 'undefined') window.localStorage.setItem('tenant_id', config.id);
      setTenantIdState(config.id);
    },
    isModuleEnabled: (moduleKey) => Boolean(tenantId) && modules.some((module) => module.key === moduleKey || module.id === moduleKey)
  }), [tenantId, tenant, modules, loading, error]);

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export function useTenant() {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
}
